import { Component } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TechStack } from '@aptsi-types';
import { TechStackControlService } from '../tech-stack-control.service';
import { MatDialog } from '@angular/material/dialog';
import { KeycloakStateService } from 'src/app/core/service/utility.service';
import { ErrorDialogComponent } from '../../shared/dialogs/error-dialog/error-dialog.component';
import { SuccessDialogComponent } from '../../shared/dialogs/success-dialog/success-dialog.component';

@Component({
  selector: 'app-tech-stack-control-create-or-update',
  templateUrl: './tech-stack-control-create-or-update.component.html',
  styleUrls: ['./tech-stack-control-create-or-update.component.scss']
})
export class TechStackControlCreateOrUpdateComponent {
 // ─────────────────────────────
  // User Context
  // ─────────────────────────────
  isCreate = false;
  existingTechStackControlId: string = "";

  // ─────────────────────────────
  // Data
  // ─────────────────────────────
techStackControl: TechStack = {
  id: '',
  name: '', 
  type: ''
};
  variables: string[] = [];
  filteredVariables: string[] = [];

  // ─────────────────────────────
  // Formula & Search
  // ─────────────────────────────
  searchTerm: string = "";
  formulaTokens: string[] = [];
  currentFormula: string = "";
  customString: string = "";
  isNumber: boolean = false;

  // ─────────────────────────────
  // UI Config
  // ─────────────────────────────
  readonly typeOptions = ["Data", "Infrastructure", "Application", "None"];
  form: FormGroup;

  // ─────────────────────────────
  // Lifecycle
  // ─────────────────────────────
  constructor(
    private route: ActivatedRoute,
    private techStackControlService: TechStackControlService,
    private dialog: MatDialog,
    private router: Router,
    private keycloakStateService: KeycloakStateService
  ) { }

  async ngOnInit(): Promise<void> {
  
    this.isCreate = Boolean(this.route.snapshot.data.create);
    this.existingTechStackControlId = this.route.snapshot.params.techStackControlId;

    if (!this.isCreate) {
      this.fetchRules();
    }

    this.form = this.isCreate
      ? new FormGroup({
        techStackControlId: new FormControl(null),
        name: new FormControl(null, Validators.required),
        type: new FormControl("None", [
          Validators.required,
          this.typeValidator,
        ])
      })
      : new FormGroup({
        techStackControlId: new FormControl(this.existingTechStackControlId, Validators.required),
        name: new FormControl(null, Validators.required),
        type: new FormControl("None", [
          Validators.required,
          this.typeValidator,
        ])
      });
  }

 
  // ─────────────────────────────
  // Search & Filtering
  // ─────────────────────────────
  onSearchChange() {
    this.filteredVariables = this.variables.filter((variable) =>
      variable.toLowerCase().includes(this.searchTerm.toLowerCase())
    );
  }

  // ─────────────────────────────
  // Data Fetching
  // ─────────────────────────────
  async fetchRules() {
    try {
      const response = await this.techStackControlService
        .getTechStackControl(this.existingTechStackControlId)
        .toPromise();
      this.techStackControl = response["Resource retrieved successfully"]?.[0];

      if (this.techStackControl) {
        this.form.patchValue({
          name: this.techStackControl.name,
          type: this.techStackControl.type
        });

      }
    } catch (error) {
      console.error("Error fetching rules:", error);
    }
  }

  // ─────────────────────────────
  // Validation
  // ─────────────────────────────
  typeValidator(control: AbstractControl): { [key: string]: any } | null {
    return control.value === "None" ? { noneSelected: true } : null;
  }

  // ─────────────────────────────
  // Submit Logic
  // ─────────────────────────────
  async submit() {
    try {
      this.form.get("techStackControlId").setErrors(null);

      if (this.isCreate) {
        await this.techStackControlService
          .createTechStackControl(
            this.form.value.name,
            this.form.value.type
            
          )
          .toPromise();
      } else {
        await this.techStackControlService
          .updateTechStackControl(
            this.existingTechStackControlId,
            this.form.value.name,
            this.form.value.type,
          )
          .toPromise();
      }

      this.openSuccessDialog(this.isCreate);
    } catch (error: any) {
      this.openErrorDialog(error.message || "An unexpected error occurred.");
    }
  }

  // ─────────────────────────────
  // Navigation
  // ─────────────────────────────
  cancel() {
    this.router.navigate([
      "insight-egrc/auth-tools/tech-stack-control/search",
    ]);
  }


  // ─────────────────────────────
  // Dialogs
  // ─────────────────────────────
  openSuccessDialog(isCreate: boolean): void {
    this.dialog
      .open(SuccessDialogComponent, {
        width: "250px",
        data: { isCreate, title: "Tech Stack Control" },
      })
      .afterClosed()
      .subscribe(() => {
        this.router.navigate([
          "insight-egrc/auth-tools/tech-stack-control/search",
        ]);
      });
  }

  openErrorDialog(errorMessage: string): void {
    this.dialog.open(ErrorDialogComponent, {
      width: "250px",
      data: { errorMessage },
    });
  }

}
